import React, { useState, useEffect } from "react";
import { Home, BarChart3, Info, Sparkles } from "lucide-react";

interface NavigationItem {
  label: string;
  href: string;
  sectionId: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface NavigationSidebarProps {
  navigationItems: NavigationItem[];
  className?: string;
}

const NavigationSidebar = ({
  navigationItems,
  className = "",
}: NavigationSidebarProps) => {
  const [activeSection, setActiveSection] = useState<string>("");

  const defaultIcons = {
    case: Home,
    studio: Sparkles,
    community: Info,
    venture: BarChart3,
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = navigationItems.map((item) => item.sectionId);

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom > 100) {
            setActiveSection(sectionId);
            return;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [navigationItems]);

  const handleNavClick = (item: NavigationItem) => {
    const element = document.getElementById(item.sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const getIcon = (item: NavigationItem, index: number) => {
    if (item.icon) return item.icon;

    const iconKeys = Object.keys(defaultIcons);
    const iconKey = iconKeys[
      index % iconKeys.length
    ] as keyof typeof defaultIcons;
    return defaultIcons[iconKey];
  };

  return (
    <div
      className={`fixed left-[70px] top-[48%] -translate-y-1/2 z-30 hidden 2xl:block ${className}`}
    >
      <div className="bg-white/90 backdrop-blur-md border border-gray-200/50 rounded-2xl shadow-lg w-44 relative">
        {/* 네비게이션 아이템들 */}
        <nav className="px-3 py-4 space-y-2">
          {navigationItems.map((item, index) => {
            const IconComponent = getIcon(item, index);
            const isActive = activeSection === item.sectionId;

            return (
              <div
                key={item.sectionId}
                className={`
                  w-full rounded-xl transition-all duration-200 overflow-hidden
                  ${isActive ? "bg-blue-50 shadow-sm" : "hover:bg-gray-50"}
                `}
              >
                <div className="flex items-center">
                  <button
                    onClick={() => handleNavClick(item)}
                    className={`flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-200 ${
                      isActive ? "text-blue-600" : "text-black"
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                  </button>

                  <div className="flex-1 pr-3 font-serif-ko">
                    <button
                      onClick={() => handleNavClick(item)}
                      className={`w-full text-left py-3 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                        isActive
                          ? "text-blue-600 font-semibold"
                          : "text-black hover:font-semibold"
                      }`}
                    >
                      {item.label}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default NavigationSidebar;
